import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Filter,
  AlertTriangle,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  Brain,
  RefreshCw,
  Download,
  ChevronRight,
  TrendingUp,
  AlertCircle,
  FileX,
  Loader2,
  User,
  Calendar,
  Building2,
  ArrowRight,
  Sparkles,
  FileCheck,
  MessageSquare,
  Activity,
  Bell,
  BellRing,
  Timer,
  Snooze,
  CalendarClock
} from "lucide-react";

const STATUS_CONFIG = {
  new: { label: "New", color: "bg-red-500", textColor: "text-red-400" },
  categorized: { label: "Categorized", color: "bg-orange-500", textColor: "text-orange-400" },
  under_review: { label: "Under Review", color: "bg-blue-500", textColor: "text-blue-400" },
  waiting_docs: { label: "Waiting Docs", color: "bg-yellow-500", textColor: "text-yellow-400" },
  waiting_provider: { label: "Waiting Provider", color: "bg-yellow-500", textColor: "text-yellow-400" },
  ready_resubmit: { label: "Ready to Resubmit", color: "bg-cyan-500", textColor: "text-cyan-400" },
  resubmitted: { label: "Resubmitted", color: "bg-indigo-500", textColor: "text-indigo-400" },
  appeal_submitted: { label: "Appeal Submitted", color: "bg-purple-500", textColor: "text-purple-400" },
  paid: { label: "Paid", color: "bg-green-500", textColor: "text-green-400" },
  partial_paid: { label: "Partially Paid", color: "bg-emerald-500", textColor: "text-emerald-400" },
  re_denied: { label: "Re-Denied", color: "bg-red-500", textColor: "text-red-400" },
  written_off: { label: "Written Off", color: "bg-slate-500", textColor: "text-slate-400" },
  closed: { label: "Closed", color: "bg-gray-500", textColor: "text-gray-400" }
};

const PRIORITY_CONFIG = {
  critical: { label: "Critical", color: "bg-red-500", textColor: "text-red-400", icon: AlertTriangle },
  high: { label: "High", color: "bg-orange-500", textColor: "text-orange-400", icon: AlertCircle },
  medium: { label: "Medium", color: "bg-yellow-500", textColor: "text-yellow-400", icon: Clock },
  low: { label: "Low", color: "bg-slate-500", textColor: "text-slate-400", icon: CheckCircle }
};

const CATEGORY_LABELS = {
  missing_pa: "Missing PA",
  invalid_auth: "Invalid Auth",
  auth_mismatch: "Auth Mismatch",
  code_mismatch: "Code Mismatch",
  medical_necessity: "Medical Necessity",
  missing_docs: "Missing Docs",
  eligibility: "Eligibility",
  coverage_inactive: "Coverage Inactive",
  patient_info: "Patient Info",
  npi_mismatch: "NPI Mismatch",
  duplicate: "Duplicate",
  timely_filing: "Timely Filing",
  modifier: "Modifier",
  bundling: "Bundling",
  policy_mismatch: "Policy Mismatch",
  referral_required: "Referral Required",
  cob: "COB",
  form_error: "Form Error",
  other: "Other"
};

export default function DenialWorkbench() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [claims, setClaims] = useState([]);
  const [reminders, setReminders] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemindersPanel, setShowRemindersPanel] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingAppeal, setGeneratingAppeal] = useState(false);

  const [newClaim, setNewClaim] = useState({
    claim_id: "",
    patient_name: "",
    member_id: "",
    insurance_payer: "",
    provider_name: "",
    date_of_service: "",
    denied_amount: "",
    procedure_codes: "",
    diagnosis_codes: "",
    denial_reason_code: "",
    denial_description: "",
    resubmission_deadline: ""
  });

  const fetchData = async () => {
    try {
      const [dashboardRes, claimsRes, remindersRes] = await Promise.all([
        axios.get(`${API}/denied-claims/dashboard`, { withCredentials: true }),
        axios.get(`${API}/denied-claims`, { withCredentials: true }),
        axios.get(`${API}/denied-claims/reminders`, { withCredentials: true })
      ]);
      setDashboard(dashboardRes.data);
      setClaims(claimsRes.data.claims || []);
      setReminders(remindersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load denied claims");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const seedDemoClaims = async () => {
    try {
      await axios.post(`${API}/denied-claims/seed-demo`, {}, { withCredentials: true });
      toast.success("Demo claims created");
      fetchData();
    } catch (error) {
      toast.error("Failed to seed demo claims");
    }
  };

  const handleCreateClaim = async () => {
    try {
      const payload = {
        ...newClaim,
        denied_amount: parseFloat(newClaim.denied_amount) || 0,
        procedure_codes: newClaim.procedure_codes.split(",").map(c => c.trim()).filter(Boolean),
        diagnosis_codes: newClaim.diagnosis_codes.split(",").map(c => c.trim()).filter(Boolean)
      };
      
      await axios.post(`${API}/denied-claims`, payload, { withCredentials: true });
      toast.success("Denied claim created with AI analysis");
      setShowAddModal(false);
      setNewClaim({
        claim_id: "", patient_name: "", member_id: "", insurance_payer: "",
        provider_name: "", date_of_service: "", denied_amount: "",
        procedure_codes: "", diagnosis_codes: "", denial_reason_code: "",
        denial_description: "", resubmission_deadline: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create claim");
    }
  };

  const handleReanalyze = async (claimId) => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/denied-claims/${claimId}/reanalyze`, {}, { withCredentials: true });
      toast.success("AI analysis complete");
      if (selectedClaim?.denied_claim_id === claimId) {
        setSelectedClaim({ ...selectedClaim, ai_analysis: res.data.ai_analysis, priority: res.data.priority?.priority });
      }
      fetchData();
    } catch (error) {
      toast.error("Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateAppeal = async (claimId) => {
    setGeneratingAppeal(true);
    try {
      const res = await axios.post(`${API}/denied-claims/${claimId}/generate-appeal`, {}, { withCredentials: true });
      toast.success("Appeal letter generated");
      if (selectedClaim?.denied_claim_id === claimId) {
        setSelectedClaim({ ...selectedClaim, appeal_letter: res.data.appeal_letter });
      }
    } catch (error) {
      toast.error("Failed to generate appeal");
    } finally {
      setGeneratingAppeal(false);
    }
  };

  const handleUpdateStatus = async (claimId, newStatus) => {
    try {
      await axios.patch(`${API}/denied-claims/${claimId}`, { status: newStatus }, { withCredentials: true });
      toast.success("Status updated");
      fetchData();
      if (selectedClaim?.denied_claim_id === claimId) {
        setSelectedClaim({ ...selectedClaim, status: newStatus });
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDownloadPacket = async (claimId) => {
    try {
      const response = await axios.get(`${API}/denied-claims/${claimId}/resubmission-packet`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `resubmission_packet_${claimId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Resubmission packet downloaded");
    } catch (error) {
      toast.error("Failed to download packet");
    }
  };

  const handleSnoozeReminder = async (claimId, days = 1) => {
    try {
      await axios.post(`${API}/denied-claims/${claimId}/snooze-reminder`, { days }, { withCredentials: true });
      toast.success(`Reminder snoozed for ${days} day(s)`);
      fetchData();
    } catch (error) {
      toast.error("Failed to snooze reminder");
    }
  };

  const filteredClaims = claims.filter(c => {
    if (searchTerm && !c.claim_id?.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !c.patient_name?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filterStatus !== "all" && c.status !== filterStatus) return false;
    if (filterPriority !== "all" && c.priority !== filterPriority) return false;
    return true;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="denial-workbench">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Denial Recovery Workbench</h1>
            <p className="text-slate-400">AI-powered denied claims management</p>
          </div>
          <div className="flex gap-3">
            {/* Reminders Button */}
            <Button 
              variant="outline" 
              onClick={() => setShowRemindersPanel(!showRemindersPanel)}
              className={`border-slate-700 relative ${showRemindersPanel ? 'bg-slate-800' : ''}`}
            >
              <Bell className="w-4 h-4 mr-2 text-yellow-400" />
              <span className="text-slate-300">Reminders</span>
              {reminders?.summary?.total_active_reminders > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                  {reminders.summary.total_active_reminders}
                </span>
              )}
            </Button>
            <Button variant="outline" onClick={seedDemoClaims} className="border-slate-700 text-slate-300">
              <Plus className="w-4 h-4 mr-2" />
              Seed Demo Claims
            </Button>
            <Button onClick={() => setShowAddModal(true)} className="bg-emerald-600 hover:bg-emerald-500">
              <Plus className="w-4 h-4 mr-2" />
              Add Denied Claim
            </Button>
          </div>
        </div>

        {/* Deadline Reminders Alert */}
        {reminders?.summary?.total_active_reminders > 0 && (
          <Card className={`border ${reminders.summary.overdue_count > 0 ? 'bg-red-500/10 border-red-500/30' : reminders.summary.critical_count > 0 ? 'bg-orange-500/10 border-orange-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${reminders.summary.overdue_count > 0 ? 'bg-red-500/20' : reminders.summary.critical_count > 0 ? 'bg-orange-500/20' : 'bg-yellow-500/20'}`}>
                    <BellRing className={`w-5 h-5 ${reminders.summary.overdue_count > 0 ? 'text-red-400' : reminders.summary.critical_count > 0 ? 'text-orange-400' : 'text-yellow-400'} animate-pulse`} />
                  </div>
                  <div>
                    <p className="font-semibold text-white">
                      {reminders.summary.overdue_count > 0 
                        ? `${reminders.summary.overdue_count} Overdue Deadline${reminders.summary.overdue_count > 1 ? 's' : ''}!` 
                        : reminders.summary.critical_count > 0 
                        ? `${reminders.summary.critical_count} Critical Deadline${reminders.summary.critical_count > 1 ? 's' : ''} (≤3 days)`
                        : `${reminders.summary.urgent_count + reminders.summary.upcoming_count} Upcoming Deadlines`}
                    </p>
                    <p className="text-sm text-slate-400">
                      {reminders.summary.total_amount_at_risk > 0 && (
                        <span className="text-red-400 font-medium">${reminders.summary.total_amount_at_risk.toLocaleString()}</span>
                      )} at risk • Click "Reminders" to view all
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowRemindersPanel(true)}
                  className={`${reminders.summary.overdue_count > 0 ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' : reminders.summary.critical_count > 0 ? 'border-orange-500/50 text-orange-400 hover:bg-orange-500/10' : 'border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10'}`}
                >
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reminders Panel (Expandable) */}
        {showRemindersPanel && reminders && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <CalendarClock className="w-5 h-5 text-yellow-400" />
                  Resubmission Deadline Reminders
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowRemindersPanel(false)} className="text-slate-400">
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Overdue */}
                {reminders.reminders.overdue.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Overdue ({reminders.reminders.overdue.length})
                    </h4>
                    <div className="space-y-2">
                      {reminders.reminders.overdue.map((r) => (
                        <div key={r.denied_claim_id} className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                          <div className="flex-1">
                            <p className="text-white font-medium">{r.patient_name} - <span className="font-mono text-sm">{r.claim_id}</span></p>
                            <p className="text-xs text-red-400">{r.message} • ${r.denied_amount?.toLocaleString()}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" onClick={() => handleSnoozeReminder(r.denied_claim_id, 1)} className="text-slate-400 hover:text-white">
                              <Timer className="w-3 h-3 mr-1" /> +1d
                            </Button>
                            <Button size="sm" onClick={() => { setSelectedClaim(claims.find(c => c.denied_claim_id === r.denied_claim_id)); setShowDetailModal(true); setShowRemindersPanel(false); }} className="bg-red-600 hover:bg-red-500 text-white">
                              Action
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Critical */}
                {reminders.reminders.critical.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-orange-400 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Critical - ≤3 Days ({reminders.reminders.critical.length})
                    </h4>
                    <div className="space-y-2">
                      {reminders.reminders.critical.map((r) => (
                        <div key={r.denied_claim_id} className="flex items-center justify-between p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                          <div className="flex-1">
                            <p className="text-white font-medium">{r.patient_name} - <span className="font-mono text-sm">{r.claim_id}</span></p>
                            <p className="text-xs text-orange-400">{r.message} • ${r.denied_amount?.toLocaleString()}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" onClick={() => handleSnoozeReminder(r.denied_claim_id, 1)} className="text-slate-400 hover:text-white">
                              <Timer className="w-3 h-3 mr-1" /> +1d
                            </Button>
                            <Button size="sm" onClick={() => { setSelectedClaim(claims.find(c => c.denied_claim_id === r.denied_claim_id)); setShowDetailModal(true); setShowRemindersPanel(false); }} className="bg-orange-600 hover:bg-orange-500 text-white">
                              Action
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Urgent */}
                {reminders.reminders.urgent.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-yellow-400 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Urgent - 4-7 Days ({reminders.reminders.urgent.length})
                    </h4>
                    <div className="space-y-2">
                      {reminders.reminders.urgent.slice(0, 3).map((r) => (
                        <div key={r.denied_claim_id} className="flex items-center justify-between p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                          <div className="flex-1">
                            <p className="text-white font-medium">{r.patient_name} - <span className="font-mono text-sm">{r.claim_id}</span></p>
                            <p className="text-xs text-yellow-400">{r.message} • ${r.denied_amount?.toLocaleString()}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" onClick={() => handleSnoozeReminder(r.denied_claim_id, 3)} className="text-slate-400 hover:text-white">
                              <Timer className="w-3 h-3 mr-1" /> +3d
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setSelectedClaim(claims.find(c => c.denied_claim_id === r.denied_claim_id)); setShowDetailModal(true); setShowRemindersPanel(false); }} className="border-yellow-500/50 text-yellow-400">
                              View
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming */}
                {reminders.reminders.upcoming.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Upcoming - 8-14 Days ({reminders.reminders.upcoming.length})
                    </h4>
                    <div className="text-sm text-slate-500">
                      {reminders.reminders.upcoming.slice(0, 3).map((r) => (
                        <div key={r.denied_claim_id} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                          <span>{r.patient_name} - {r.claim_id}</span>
                          <span className="text-slate-400">{r.days_until_deadline} days</span>
                        </div>
                      ))}
                      {reminders.reminders.upcoming.length > 3 && (
                        <p className="text-xs text-slate-500 mt-2">+{reminders.reminders.upcoming.length - 3} more upcoming</p>
                      )}
                    </div>
                  </div>
                )}

                {reminders.summary.total_active_reminders === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500/50" />
                    <p>All caught up! No urgent deadlines.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <FileX className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{dashboard?.summary?.total_claims || 0}</p>
                  <p className="text-xs text-slate-400">Total Denials</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">${((dashboard?.summary?.total_denied_amount || 0) / 1000).toFixed(1)}K</p>
                  <p className="text-xs text-slate-400">Denied Amount</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">${((dashboard?.summary?.total_recovered || 0) / 1000).toFixed(1)}K</p>
                  <p className="text-xs text-slate-400">Recovered</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{dashboard?.action_required?.needs_review || 0}</p>
                  <p className="text-xs text-slate-400">Needs Review</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Aging Buckets */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">Denial Aging</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-4">
              {Object.entries(dashboard?.aging_buckets || {}).map(([bucket, count]) => (
                <div key={bucket} className="text-center p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-lg font-bold text-white">{count}</p>
                  <p className="text-xs text-slate-400">{bucket} days</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Claims Table */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-white">Denied Claims</CardTitle>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search claims..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-48 bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    {Object.entries(PRIORITY_CONFIG).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Claim</TableHead>
                  <TableHead className="text-slate-400">Patient</TableHead>
                  <TableHead className="text-slate-400">Payer</TableHead>
                  <TableHead className="text-slate-400">Amount</TableHead>
                  <TableHead className="text-slate-400">Category</TableHead>
                  <TableHead className="text-slate-400">Priority</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClaims.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                      No denied claims found. Click "Seed Demo Claims" to add sample data.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClaims.map((claim) => {
                    const statusConfig = STATUS_CONFIG[claim.status] || STATUS_CONFIG.new;
                    const priorityConfig = PRIORITY_CONFIG[claim.priority] || PRIORITY_CONFIG.medium;
                    const PriorityIcon = priorityConfig.icon;
                    
                    return (
                      <TableRow 
                        key={claim.denied_claim_id} 
                        className="border-slate-700 hover:bg-slate-800/50 cursor-pointer"
                        onClick={() => { setSelectedClaim(claim); setShowDetailModal(true); }}
                        data-testid={`claim-row-${claim.denied_claim_id}`}
                      >
                        <TableCell>
                          <p className="font-mono text-sm text-slate-300">{claim.claim_id}</p>
                          <p className="text-xs text-slate-500">{claim.date_of_service}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-white">{claim.patient_name}</p>
                          <p className="text-xs text-slate-500">{claim.member_id}</p>
                        </TableCell>
                        <TableCell className="text-slate-300">{claim.insurance_payer?.slice(0, 20)}...</TableCell>
                        <TableCell className="font-semibold text-red-400">${claim.denied_amount?.toLocaleString()}</TableCell>
                        <TableCell>
                          {claim.category ? (
                            <Badge variant="outline" className="border-slate-600 text-slate-300">
                              {CATEGORY_LABELS[claim.category] || claim.category}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-yellow-500/30 text-yellow-400">
                              Pending AI
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className={`flex items-center gap-1 ${priorityConfig.textColor}`}>
                            <PriorityIcon className="w-3 h-3" />
                            <span className="text-sm">{priorityConfig.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusConfig.color} text-white`}>
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setSelectedClaim(claim); setShowDetailModal(true); }}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Claim Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-800 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Claim: {selectedClaim?.claim_id}</span>
                <Badge className={`${STATUS_CONFIG[selectedClaim?.status]?.color || 'bg-slate-500'} text-white`}>
                  {STATUS_CONFIG[selectedClaim?.status]?.label || selectedClaim?.status}
                </Badge>
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Review claim details, AI analysis, workflow status, and generate appeal letters.
              </DialogDescription>
            </DialogHeader>

            {selectedClaim && (
              <Tabs defaultValue="details" className="mt-4">
                <TabsList className="bg-slate-800">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="ai">AI Analysis</TabsTrigger>
                  <TabsTrigger value="workflow">Workflow</TabsTrigger>
                  <TabsTrigger value="appeal">Appeal</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3 p-4 bg-slate-800/50 rounded-lg">
                      <h4 className="font-semibold text-slate-300 flex items-center gap-2">
                        <User className="w-4 h-4" /> Patient Info
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-slate-500">Name:</span> <span className="text-white">{selectedClaim.patient_name}</span></p>
                        <p><span className="text-slate-500">Member ID:</span> <span className="text-white">{selectedClaim.member_id}</span></p>
                        <p><span className="text-slate-500">Payer:</span> <span className="text-white">{selectedClaim.insurance_payer}</span></p>
                      </div>
                    </div>
                    <div className="space-y-3 p-4 bg-slate-800/50 rounded-lg">
                      <h4 className="font-semibold text-slate-300 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" /> Claim Info
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-slate-500">Amount:</span> <span className="text-red-400 font-semibold">${selectedClaim.denied_amount?.toLocaleString()}</span></p>
                        <p><span className="text-slate-500">Service Date:</span> <span className="text-white">{selectedClaim.date_of_service}</span></p>
                        <p><span className="text-slate-500">Provider:</span> <span className="text-white">{selectedClaim.provider_name}</span></p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <h4 className="font-semibold text-red-400 mb-2">Denial Reason</h4>
                    <p className="text-slate-300"><span className="font-mono text-red-400">{selectedClaim.denial_reason_code}</span> - {selectedClaim.denial_description}</p>
                  </div>

                  <div className="flex gap-3">
                    <Select value={selectedClaim.status} onValueChange={(val) => handleUpdateStatus(selectedClaim.denied_claim_id, val)}>
                      <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={() => handleDownloadPacket(selectedClaim.denied_claim_id)} className="border-slate-700">
                      <Download className="w-4 h-4 mr-2" /> Resubmission Packet
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="ai" className="space-y-4 mt-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-slate-300 flex items-center gap-2">
                      <Brain className="w-4 h-4 text-purple-400" /> AI Denial Analysis
                    </h4>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleReanalyze(selectedClaim.denied_claim_id)}
                      disabled={analyzing}
                      className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                    >
                      {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Re-analyze
                    </Button>
                  </div>

                  {selectedClaim.ai_analysis ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                        <h5 className="font-medium text-purple-400 mb-2">Summary</h5>
                        <p className="text-slate-300">{selectedClaim.ai_analysis.denial_summary}</p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-4 bg-slate-800/50 rounded-lg">
                          <h5 className="font-medium text-slate-300 mb-2">Category</h5>
                          <Badge className="bg-indigo-500">{CATEGORY_LABELS[selectedClaim.ai_analysis.category] || selectedClaim.ai_analysis.category}</Badge>
                        </div>
                        <div className="p-4 bg-slate-800/50 rounded-lg">
                          <h5 className="font-medium text-slate-300 mb-2">Success Probability</h5>
                          <div className="flex items-center gap-3">
                            <Progress value={selectedClaim.ai_analysis.success_probability || 0} className="flex-1" />
                            <span className="text-emerald-400 font-semibold">{selectedClaim.ai_analysis.success_probability}%</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <h5 className="font-medium text-emerald-400 mb-2">Recommended Action</h5>
                        <p className="text-slate-300">{selectedClaim.ai_analysis.recommended_action}</p>
                      </div>

                      {selectedClaim.ai_analysis.action_steps?.length > 0 && (
                        <div className="p-4 bg-slate-800/50 rounded-lg">
                          <h5 className="font-medium text-slate-300 mb-3">Action Steps</h5>
                          <ul className="space-y-2">
                            {selectedClaim.ai_analysis.action_steps.map((step, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
                                <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs flex-shrink-0">{i + 1}</span>
                                {step}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No AI analysis available. Click "Re-analyze" to generate.</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="workflow" className="space-y-4 mt-4">
                  <h4 className="font-semibold text-slate-300 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-cyan-400" /> Recovery Workflow
                  </h4>

                  {selectedClaim.workflow?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedClaim.workflow.map((step, i) => (
                        <div 
                          key={i}
                          className={`p-4 rounded-lg border ${
                            step.status === 'completed' ? 'bg-emerald-500/10 border-emerald-500/30' :
                            step.status === 'in_progress' ? 'bg-blue-500/10 border-blue-500/30' :
                            'bg-slate-800/50 border-slate-700'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              step.status === 'completed' ? 'bg-emerald-500' :
                              step.status === 'in_progress' ? 'bg-blue-500' :
                              'bg-slate-700'
                            }`}>
                              {step.status === 'completed' ? (
                                <CheckCircle className="w-4 h-4 text-white" />
                              ) : (
                                <span className="text-white text-sm">{step.step}</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${step.status === 'completed' ? 'text-emerald-400' : step.status === 'in_progress' ? 'text-blue-400' : 'text-slate-300'}`}>
                                {step.title}
                              </p>
                              <p className="text-sm text-slate-500">{step.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Workflow will be generated after AI categorization</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="appeal" className="space-y-4 mt-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-slate-300 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-indigo-400" /> Appeal Letter
                    </h4>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleGenerateAppeal(selectedClaim.denied_claim_id)}
                      disabled={generatingAppeal}
                      className="border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10"
                    >
                      {generatingAppeal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                      Generate Appeal
                    </Button>
                  </div>

                  {selectedClaim.appeal_letter ? (
                    <div className="p-4 bg-slate-800/50 rounded-lg">
                      <pre className="text-sm text-slate-300 whitespace-pre-wrap font-sans">{selectedClaim.appeal_letter}</pre>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No appeal letter generated yet. Click "Generate Appeal" to create one.</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Claim Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-2xl bg-slate-900 border-slate-800 text-white">
            <DialogHeader>
              <DialogTitle>Add Denied Claim</DialogTitle>
              <DialogDescription className="text-slate-400">
                Enter the denied claim details. AI will automatically categorize and analyze the denial.
              </DialogDescription>
            </DialogHeader>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label>Claim ID</Label>
                <Input
                  value={newClaim.claim_id}
                  onChange={(e) => setNewClaim({ ...newClaim, claim_id: e.target.value })}
                  placeholder="CLM-2024-XXXXX"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Patient Name</Label>
                <Input
                  value={newClaim.patient_name}
                  onChange={(e) => setNewClaim({ ...newClaim, patient_name: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Member ID</Label>
                <Input
                  value={newClaim.member_id}
                  onChange={(e) => setNewClaim({ ...newClaim, member_id: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Insurance Payer</Label>
                <Input
                  value={newClaim.insurance_payer}
                  onChange={(e) => setNewClaim({ ...newClaim, insurance_payer: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Provider Name</Label>
                <Input
                  value={newClaim.provider_name}
                  onChange={(e) => setNewClaim({ ...newClaim, provider_name: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Date of Service</Label>
                <Input
                  type="date"
                  value={newClaim.date_of_service}
                  onChange={(e) => setNewClaim({ ...newClaim, date_of_service: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Denied Amount ($)</Label>
                <Input
                  type="number"
                  value={newClaim.denied_amount}
                  onChange={(e) => setNewClaim({ ...newClaim, denied_amount: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Resubmission Deadline</Label>
                <Input
                  type="date"
                  value={newClaim.resubmission_deadline}
                  onChange={(e) => setNewClaim({ ...newClaim, resubmission_deadline: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Procedure Codes (comma-separated)</Label>
                <Input
                  value={newClaim.procedure_codes}
                  onChange={(e) => setNewClaim({ ...newClaim, procedure_codes: e.target.value })}
                  placeholder="99213, 71046"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Diagnosis Codes (comma-separated)</Label>
                <Input
                  value={newClaim.diagnosis_codes}
                  onChange={(e) => setNewClaim({ ...newClaim, diagnosis_codes: e.target.value })}
                  placeholder="J06.9, R05"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Denial Reason Code</Label>
                <Input
                  value={newClaim.denial_reason_code}
                  onChange={(e) => setNewClaim({ ...newClaim, denial_reason_code: e.target.value })}
                  placeholder="CO-197"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            <div className="space-y-2 mt-4">
              <Label>Denial Description</Label>
              <Textarea
                value={newClaim.denial_description}
                onChange={(e) => setNewClaim({ ...newClaim, denial_description: e.target.value })}
                placeholder="Describe the denial reason..."
                className="bg-slate-800 border-slate-700"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-slate-700">
                Cancel
              </Button>
              <Button onClick={handleCreateClaim} className="bg-emerald-600 hover:bg-emerald-500">
                <Plus className="w-4 h-4 mr-2" /> Create & Analyze
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
